import * as React from 'react'
import {
  Button,
  Modal,
  ModalActions,
  ModalContent,
  ModalHeader,
  Text,
  Inline,
  ToastNotifications,
} from '@lightspeed/design-system-react'
import { trackUserEvent } from '@vend/utilities'
import { ApiCustomer } from '@vend/types'
import { useT } from '@transifex/react'
import { STEPS } from '../types'
import { CustomerInfo, PartialCustomerGroup } from '../../../common/types'
import { StepBreadcrumbs } from '../StepBreadcrumbs'
import {
  ServiceAgenda,
  ServiceFields,
  ServiceItemData,
} from '../../../../../../models/sale.constants'
import { toHoursAndMinutes } from '../../../../../../util/agenda-utils'
import { MIN_HOURS_AND_MINUTES } from '../../constants'
import { isServiceScheduled } from '../../utilities'
import { useAppDispatch, useAppSelector } from '../../../../../../state/hooks'
import {
  useSaleManager,
  useCustomerManager,
} from '../../../../../../ng-compat/hooks'
import {
  selectSaleCustomer,
  selectSaleNote,
  setSaleNote,
  upsertServiceFields,
} from '../../../../../../state/slices/currentSale/currentSaleSlice'
import { trimContactInfo } from '../../../common/customer-contact-utils'
import { ServiceScheduling } from './ServiceScheduling'

const CANNOT_SAVE_CUSTOMER = 'Cannot save customer'

interface Props {
  onClose: (shouldCreate?: boolean) => void
  onStepChange: (step: STEPS) => void
  onServiceChange: (serviceAgenda?: ServiceAgenda) => void
  group?: PartialCustomerGroup
  newCustomer: CustomerInfo | null
  contactChanged: boolean
  serviceAgenda?: ServiceAgenda
  serviceItem?: ServiceItemData
  selectedCustomer: ApiCustomer | null
  note: string
  isEditing: boolean
}

// The agenda minutes and date inputs
export const StepSchedule: React.FC<Props> = ({
  onClose,
  onStepChange,
  onServiceChange,
  group,
  contactChanged,
  newCustomer,
  serviceItem,
  note,
  serviceAgenda: initialServiceAgenda,
  selectedCustomer,
  isEditing,
}) => {
  const t = useT()
  const selectDateErrorMessages = {
    noDateWithTime: t('Select a date or clear to continue without scheduling', {
      _context: 'Error message in service scheduling step.',
    }),
    dateWithoutTime: t('Enter a time or clear to continue without scheduling', {
      _context: 'Error message in service scheduling step.',
    }),
  }
  const stepScheduleCopies = {
    clearSchedule: t(`Clear scheduling`, {
      _context: 'Text for scheduling step in service modal.',
    }),
    back: t('Back', {
      _context: 'Back button text for scheduling step in service modal.',
    }),
    updateService: t('Update service', {
      _context: 'Button text for scheduling step in service modal.',
    }),
    createService: t('Create service', {
      _context: 'Button text for scheduling step in service modal.',
    }),
  }

  const [serviceAgenda, setServiceAgenda] = React.useState<
    ServiceAgenda | undefined
  >(initialServiceAgenda)
  const dispatch = useAppDispatch()
  const saleCustomer = useAppSelector(selectSaleCustomer)
  const saleNote = useAppSelector(selectSaleNote)

  const [selectDateError, setSelectDateError] = React.useState<string | null>(
    null
  )
  const [
    shouldDisableCreateService,
    setShouldDisableCreateService,
  ] = React.useState<boolean>(false)

  const { hours, minutes } = toHoursAndMinutes(
    serviceAgenda?.minutesScheduled || MIN_HOURS_AND_MINUTES
  )

  const [hoursInput, setHoursInput] = React.useState<number | null>(hours)
  const [minutesInput, setMinutesInput] = React.useState<number | null>(minutes)
  const [selectedDate, setSelectedDate] = React.useState<string | undefined>(
    serviceAgenda?.date
  )
  const [loading, setLoading] = React.useState<boolean>(false)

  const [validationError, setValidationError] = React.useState<string[]>([])

  const saleManager = useSaleManager()

  const customerManager = useCustomerManager()

  const clearSchedule = () => {
    setSelectedDate('')
    setHoursInput(null)
    setMinutesInput(null)
    trackUserEvent(
      'Services',
      'sale.service_sale.new_sale.service_details.clear_schedule.click'
    )
  }

  React.useEffect(() => {
    setShouldDisableCreateService(false)
  }, [hoursInput, minutesInput, selectedDate])

  const handleServiceAgendaChange = (value: ServiceAgenda) => {
    setServiceAgenda(value)
  }

  const handleBack = () => {
    onServiceChange(serviceAgenda)
    onStepChange(STEPS.WORK_DESCRIPTION)
  }

  const handleCreateService = async () => {
    if (
      !validationError.length &&
      !selectedDate &&
      ((hoursInput && hoursInput > 0) || (minutesInput && minutesInput > 0))
    ) {
      setShouldDisableCreateService(true)
      setSelectDateError(selectDateErrorMessages.noDateWithTime)
      return
    }
    // Date is selected, but hours and minutes are 0
    if (selectedDate && !hoursInput && !minutesInput) {
      setShouldDisableCreateService(true)
      setSelectDateError(selectDateErrorMessages.dateWithoutTime)
      return
    }
    onServiceChange(serviceAgenda)

    trackUserEvent(
      'Services',
      'sale.service_sale.new_sale.service_details.create_service.click',
      { is_scheduled: isServiceScheduled(serviceAgenda) }
    )

    try {
      const customerPromise = selectedCustomer
        ? handleCustomerUpdate
        : handleCustomerCreate

      await customerPromise()

      if (note !== saleNote) {
        dispatch(setSaleNote(note))
      }

      // Only agenda and items can be updated via UI
      const serviceFields: Pick<
        ServiceFields,
        'serviceAgenda' | 'serviceItem'
      > = {}

      // Scheduling is optional, so only set if it's been scheduled
      if (
        serviceAgenda &&
        Object.keys(serviceAgenda).length > 0 &&
        serviceAgenda.date &&
        serviceAgenda.minutesScheduled
      ) {
        serviceFields.serviceAgenda = serviceAgenda
      }

      // Not mandatory that services have a service item
      if (serviceItem) {
        serviceFields.serviceItem = serviceItem
      }

      dispatch(upsertServiceFields(serviceFields))
      await saleManager.saveSale()
      onClose(true)
    } catch (error) {
      setLoading(false)

      if (error && error !== CANNOT_SAVE_CUSTOMER) {
        ToastNotifications.negative('Failed to create service order')
      }
    }
  }

  // Convert added Customer to ApiCustomer format
  const convertCustomerToApiCustomer = (
    customer: CustomerInfo | null,
    group: PartialCustomerGroup
  ) => {
    if (customer) {
      const result = {
        first_name: customer.firstName && customer.firstName.trim(),
        last_name: customer.lastName && customer.lastName.trim(),
        mobile: customer.mobile,
        email: customer.email,
        phone: customer.phone,
        fax: customer.fax,
        customer_group_id: group?.id,
      } as ApiCustomer

      trimContactInfo(result)
      return result
    }
    return null
  }

  const handleCustomerCreate = () => {
    return new Promise<void>((resolve, reject) => {
      const customer = convertCustomerToApiCustomer(newCustomer, group!)
      if (customer) {
        setLoading(true)
        return (
          customerManager
            .saveNewCustomer(customer)
            .then(savedCustomer => saleManager.addCustomer(savedCustomer))
            .then(() => resolve())
            // customerManager will throw the error
            // eslint-disable-next-line prefer-promise-reject-errors
            .catch(() => reject(CANNOT_SAVE_CUSTOMER))
        )
      }
    })
  }

  const handleCustomerUpdate = () => {
    return new Promise<void>((resolve, reject) => {
      setLoading(true)
      if (contactChanged) {
        trimContactInfo(selectedCustomer!)
        return (
          customerManager
            .updateCustomer(selectedCustomer!)
            .then(savedCustomer => saleManager.addCustomer(savedCustomer))
            .then(() => resolve())
            // eslint-disable-next-line prefer-promise-reject-errors
            .catch(() => reject(CANNOT_SAVE_CUSTOMER))
        )
      } else if (saleCustomer && saleCustomer.id === selectedCustomer!.id) {
        // Didn't change pre-attached customer
        return resolve()
      } else {
        // Selected/changed a customer at StepSelectCustomer, but didn't update contact info
        return saleManager.addCustomer(selectedCustomer!).then(() => resolve())
      }
    })
  }

  return (
    <Modal onClose={() => onClose()}>
      <StepBreadcrumbs step={STEPS.SCHEDULE} />
      <ModalHeader>
        {t('Schedule the service (optional)', {
          _context:
            'The header of the modal step when creating a service (if a certain feature flag is enabled).',
        })}
      </ModalHeader>
      <ModalContent>
        <ServiceScheduling
          setValidationError={setValidationError}
          validationError={validationError}
          onServiceAgendaChange={handleServiceAgendaChange}
          serviceAgenda={serviceAgenda}
          selectDateError={selectDateError}
          shouldDisableCreateService={shouldDisableCreateService}
          selectedDate={selectedDate}
          setSelectedDate={setSelectedDate}
          hoursInput={hoursInput}
          setHoursInput={setHoursInput}
          minutesInput={minutesInput}
          setMinutesInput={setMinutesInput}
        />
      </ModalContent>
      <ModalActions className="vd-flex vd-flex--justify-between vd-flex--align-center">
        <Text
          align="left"
          className="clear-schedule vd-mr-4"
          onClick={clearSchedule}
          data-testid="service-scheduling-clear"
        >
          {stepScheduleCopies.clearSchedule}
        </Text>

        <Inline align="right" className="vd-btn-group" space={0}>
          <Button
            variant="supplementary"
            onClick={handleBack}
            disabled={loading}
            data-cy="button-back"
            data-track="back-schedule-step"
          >
            {stepScheduleCopies.back}
          </Button>
          <Button
            variant="go"
            onClick={handleCreateService}
            loading={loading}
            data-cy="button-create-service"
            disabled={!!validationError.length || shouldDisableCreateService}
            data-track={isEditing ? 'update-service' : 'create-service'}
          >
            {isEditing
              ? stepScheduleCopies.updateService
              : stepScheduleCopies.createService}
          </Button>
        </Inline>
      </ModalActions>
    </Modal>
  )
}

StepSchedule.displayName = 'StepSchedule'
